import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { loginUser, logoutUser } from "../thunks/authThunks";
import { setUserImage, updateUserInfo } from "../thunks/userThunks";
import { User } from "../../models/User.model";
import { Company } from "../../models/Company.model";

export interface CompanyIdentType {
  identType_Code: number;
  identType_Name: string;
  identType_ShortName: number;
}

interface AuthState {
  isAuthenticated: boolean;
  token: string | null;
  user: User | null;
  companies: Company[];
  companyKey: string | null;
  loading: boolean;
  error: string | null;
  selectedCompanyVat: number | null;
  companyIdentTypes: CompanyIdentType[];
  loadingImage: boolean;
  errorImage: string | null;
}

const initialState: AuthState = {
  isAuthenticated: !!sessionStorage.getItem("token"),
  token: sessionStorage.getItem("token"),
  user: JSON.parse(sessionStorage.getItem("user") || "null"),
  companies: JSON.parse(sessionStorage.getItem("companies") || "[]"),
  companyKey: sessionStorage.getItem("companyKey"),
  selectedCompanyVat: (() => {
    const savedVat = sessionStorage.getItem("selectedCompanyVat");
    if (savedVat) return Number(savedVat);

    const companyId = sessionStorage.getItem("companyKey");
    const companies = JSON.parse(sessionStorage.getItem("companies") || "[]");
    const company = companies.find((c: Company) => c.companyKey === companyId);
    return company?.vat || null;
  })(),
  companyIdentTypes: JSON.parse(
    sessionStorage.getItem("companyIdentTypes") || "[]",
  ), // טעינה מ-session
  loading: false,
  error: null,
  loadingImage: false,
  errorImage: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedCompany: (state, action: PayloadAction<string>) => {
      state.companyKey = action.payload;
      const selectedCompany = state.companies.find(
        (c) => c.companyKey === action.payload,
      );
      state.selectedCompanyVat = selectedCompany?.vat || null;

      sessionStorage.setItem("companyKey", action.payload);
      if (selectedCompany?.vat) {
        sessionStorage.setItem(
          "selectedCompanyVat",
          selectedCompany.vat.toString(),
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.loading = false;
      state.isAuthenticated = true;
      state.token = action.payload.token;
      state.user = action.payload.user;
      state.companies = action.payload.companies;

      // שמירת CompanyIdentTypes
      if (action.payload.companyIdentTypes) {
        state.companyIdentTypes = action.payload.companyIdentTypes;
        sessionStorage.setItem(
          "companyIdentTypes",
          JSON.stringify(action.payload.companyIdentTypes),
        );
      }

      // אם אין חברה נבחרת, בחר את הדיפולטיבית
      if (!state.companyKey && action.payload.companies.length > 0) {
        const defaultCompany = action.payload.companies.find(
          (c) => c.isDefault,
        );
        state.companyKey =
          defaultCompany?.companyKey || action.payload.companies[0].companyKey;
        sessionStorage.setItem("companyKey", state.companyKey);
      }

      sessionStorage.setItem("token", action.payload.token);
      sessionStorage.setItem("user", JSON.stringify(action.payload.user));
      sessionStorage.setItem(
        "companies",
        JSON.stringify(action.payload.companies),
      );

      if (state.companyKey) {
        const selectedCompany = action.payload.companies.find(
          (c) => c.companyKey === state.companyKey,
        );
        state.selectedCompanyVat = selectedCompany?.vat || null;
        if (selectedCompany?.vat) {
          sessionStorage.setItem(
            "selectedCompanyVat",
            selectedCompany.vat.toString(),
          );
        }
      }
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });

    // Logout
    builder.addCase(logoutUser.fulfilled, (state) => {
      state.isAuthenticated = false;
      state.token = null;
      state.user = null;
      state.companies = [];
      state.companyKey = null;
      state.companyIdentTypes = [];

      window.location.href = "/login";
    });

    // Set User Image
    builder.addCase(setUserImage.pending, (state) => {
      state.loadingImage = true;
      state.errorImage = null;
    });
    builder.addCase(setUserImage.fulfilled, (state, action) => {
      state.loadingImage = false;
      // עדכון ה-URL ב-user גם ב-state וגם ב-sessionStorage
      if (state.user) {
        state.user = {
          ...state.user,
          imageURL: action.meta.arg.imageURL,
        };
        sessionStorage.setItem("user", JSON.stringify(state.user));
      }
    });
    builder.addCase(setUserImage.rejected, (state, action) => {
      state.loadingImage = false;
      state.errorImage = action.payload as string;
    });

    // Update User Info
    builder.addCase(updateUserInfo.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(updateUserInfo.fulfilled, (state, action) => {
      state.loading = false;
      // עדכון פרטי המשתמש ב-state וב-sessionStorage
      if (state.user) {
        const { fullName, eMail, phone } = action.meta.arg;
        state.user = {
          ...state.user,
          userFullName: fullName,
          userEmail: eMail,
          userPhone: phone || null,
        };
        sessionStorage.setItem("user", JSON.stringify(state.user));
      }
    });
    builder.addCase(updateUserInfo.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearError, setSelectedCompany } = authSlice.actions;
export default authSlice.reducer;
